import { Button, Icon, Text, useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import assert from 'assert';
import React, { useCallback } from 'react';
import { usePlaidLink, PlaidLinkOnSuccessMetadata } from 'react-plaid-link';

import { Maybe } from 'types/dashboard-core-gqlTypes.generated';
import { LinkProps } from 'types/plaid';

import {
  errorToastConfig,
  errorToastId,
  toastId,
  successToastConfig,
} from 'packages/common-frontend/constants';
import { FiPlus } from 'react-icons/fi';

export const LinkModal = ({
  linkToken,
  needsUpdate,
  institutionName,
  plaidItemId,
  handleTokenExchange,
  handleReconnectPlaidItem,
}: LinkProps) => {
  const toast = useToast();

  const handerReconnectPlaidItemHelper = useCallback(
    async (
      _plaidItemId: Maybe<string> | undefined,
      _institutionName: Maybe<string> | undefined,
    ) => {
      try {
        assert(_institutionName, 'Unable to reconnect institution');
        assert(_plaidItemId, 'Unable to reconnect institution');
        const reconnectPlaidItemResponse = (
          await handleReconnectPlaidItem(_plaidItemId)
        ).data;
        assert(
          reconnectPlaidItemResponse &&
            reconnectPlaidItemResponse.reconnectPlaidItem,
          'Unable to reconnect institution',
        );

        const { plaidResponse } = reconnectPlaidItemResponse.reconnectPlaidItem;

        if (!toast.isActive(toastId)) {
          successToastConfig.title = plaidResponse;
          toast(successToastConfig);
        }
      } catch (err) {
        if (!toast.isActive(errorToastId)) {
          errorToastConfig.title =
            'Unable to reconnect institution. Please try again';
          toast(errorToastConfig);
        }
      }
    },
    [handleReconnectPlaidItem, toast],
  );

  const onSuccess = useCallback(
    async (publicToken: string, metadata: PlaidLinkOnSuccessMetadata) => {
      // reconnect plaid item
      if (needsUpdate === true) {
        await handerReconnectPlaidItemHelper(plaidItemId, institutionName);
        return;
      }

      // exchange for access token
      try {
        assert(metadata.institution, 'Unable to integrate institution');

        const institutionId = metadata.institution.institution_id;

        if (!toast.isActive(toastId)) {
          const successToastConfigCopy = { ...successToastConfig };
          successToastConfigCopy.duration = 20000;
          successToastConfigCopy.title = 'Successfully connected.';
          toast(successToastConfigCopy);
        }

        const institutionWithAccounts = (
          await handleTokenExchange(publicToken, institutionId)
        ).data;

        assert(institutionWithAccounts, 'Unable to integrate institution');
      } catch (err) {
        if (!toast.isActive(errorToastId)) {
          errorToastConfig.title =
            'Unable to integrate institution. Please try again';
          toast(errorToastConfig);
        }
      }
    },
    [
      toast,
      handleTokenExchange,
      needsUpdate,
      plaidItemId,
      institutionName,
      handerReconnectPlaidItemHelper,
    ],
  );

  const config: Parameters<typeof usePlaidLink>[0] = {
    token: linkToken,
    onSuccess,
    onExit: async (err) => {
      if (err && err.error_code === 'item-no-error') {
        await handerReconnectPlaidItemHelper(plaidItemId, institutionName);
      }
    },
  };

  const { open, ready } = usePlaidLink(config);

  let linkText = 'Connect new account';
  if (needsUpdate === true) {
    assert(institutionName, 'Reconnect institution name is undefined');
    linkText = `Reconnect ${institutionName}`;
  }

  return (
    <Sentry.ErrorBoundary
      onError={() => {
        if (!toast.isActive(errorToastId)) {
          errorToastConfig.title =
            'Error connecting with Plaid. Please contact support@finleycms.com';
          toast(errorToastConfig);
        }
      }}
    >
      <Button
        colorScheme={needsUpdate === true ? 'red' : undefined}
        onClick={() => open()}
        isDisabled={!ready}
        mb="30px"
      >
        <Text mr="10px">{linkText}</Text>
        <Icon as={FiPlus} />
      </Button>
    </Sentry.ErrorBoundary>
  );
};
